import type { TFunction } from 'i18next';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Alert } from '@/app/components/ui/Alert';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { Skeleton } from '@/app/components/ui/Skeleton';
import { cn } from '@/lib/cn';
import { formatNumber } from '@/lib/format';
import { useThemeColors } from '@/lib/useThemeColors';
import { useAsync } from '@/lib/useAsync';
import type {
  DashboardPanelResult,
  DashboardRange,
  DashboardTarget,
  Grain,
} from '@/features/statistics/api';
import { runDashboardBatch } from '@/features/statistics/api';
import { StatusNote } from '@/features/statistics/StatsPanel';

function targetLabel(target: DashboardTarget | undefined, t: TFunction): string {
  if (!target) return '—';
  if ('user_id' in target) return t('statistics.target.you');
  if ('department_id' in target)
    return t('statistics.target.department', { name: target.department_id.slice(0, 8) });
  return t('statistics.target.company', { name: target.company_id.slice(0, 8) });
}

/**
 * One metric across several targets (departments, or a department's members)
 * side by side — a comparison panel (`target` as an array). Its own batch
 * call unless `result` is supplied. An optional `detailTo` links to the
 * full chart/table.
 */
export function CompareChart({
  title,
  metricKey,
  targets,
  grain,
  range,
  result,
  detailTo,
  className,
}: {
  title: string;
  metricKey: string;
  targets: DashboardTarget[];
  grain?: Grain;
  range?: DashboardRange;
  result?: DashboardPanelResult;
  detailTo?: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const colors = useThemeColors();
  const shouldFetch = result === undefined;

  const spec = {
    id: 'compare',
    type: 'series' as const,
    target: targets,
    grain: grain ?? 'range',
    range: range ?? 'last_30_days',
    metrics: [metricKey],
  };

  const batch = useAsync(
    () => (shouldFetch ? runDashboardBatch([spec]) : Promise.resolve(null)),
    [shouldFetch, JSON.stringify(spec)],
  );

  const resolved = result ?? (batch.data ? batch.data.panels.compare : undefined);
  const loading = shouldFetch && batch.loading;

  return (
    <Card className={cn(className)}>
      <CardHeader
        title={title}
        action={
          detailTo ? (
            <Link to={detailTo} className="text-accent text-sm">
              {t('statistics.detailLink')}
            </Link>
          ) : undefined
        }
      />
      {loading ? (
        <Skeleton kind="card" />
      ) : shouldFetch && batch.error ? (
        <Alert tone="danger">
          {t('statistics.status.loadFailed', { message: batch.error.message })}
        </Alert>
      ) : !resolved ? (
        <Skeleton kind="card" />
      ) : resolved.status !== 'ok' ? (
        <StatusNote status={resolved.status} />
      ) : (
        <CompareBars
          metricKey={metricKey}
          outcomes={resolved.targets ?? []}
          colors={colors}
          t={t}
        />
      )}
    </Card>
  );
}

function CompareBars({
  metricKey,
  outcomes,
  colors,
  t,
}: {
  metricKey: string;
  outcomes: DashboardPanelResult[];
  colors: { accent: string };
  t: TFunction;
}) {
  const data = outcomes.map((outcome, index) => ({
    name: targetLabel(outcome.target, t),
    value:
      outcome.status === 'ok'
        ? (outcome.series?.find((metric) => metric.metric_key === metricKey)?.buckets[0]?.value ??
          null)
        : null,
    status: outcome.status,
    key: index,
  }));

  if (data.every((row) => row.value === null)) {
    return (
      <Alert tone="info" role="status">
        {t('statistics.status.noData')}
      </Alert>
    );
  }

  return (
    <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid stroke={`${colors.accent}1a`} vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={(value: number) => formatNumber(value)} />
          <Tooltip
            contentStyle={{
              background: 'var(--paper)',
              border: '1px solid var(--ink)',
              borderRadius: 12,
            }}
            formatter={(value: unknown) => formatNumber(Number(value))}
          />
          <Bar dataKey="value" fill={colors.accent} radius={[6, 6, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
